import { ActionReducer } from '@ngrx/store';
import { authFeatureKey, AuthState } from './auth.state';
import {
  COLLAB_ORG_KEY,
  PERMISSION_KEY,
  PROJECT_KEY,
  TOKEN_KEY,
  USER_KEY,
  USER_ORG_KEY,
  writeJson,
  writeRaw,
} from './auth.storage-keys';

/**
 * Mirrors the auth slice into localStorage.
 *
 * A meta-reducer rather than an effect, for two reasons:
 *
 *  1. It writes synchronously, as part of the dispatch. Sign-in and org-switch
 *     tear the page down with window.location.href / reload() within a tick of
 *     dispatching, so an effect's microtask can lose the race.
 *  2. It is complete by construction — one place sees post-reduction state for
 *     every action, instead of a dozen actions each needing to remember to persist.
 */
export function persistAuthMetaReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  let previous: AuthState | undefined;

  return (state, action) => {
    const nextState = reducer(state, action);
    const auth: AuthState | undefined = nextState?.[authFeatureKey];
    if (!auth) return nextState;

    // The first pass is the hydrated state, which came out of storage moments ago.
    // Writing it straight back would be pointless, and would let a hydration bug
    // corrupt good stored data on page load.
    if (previous === undefined) {
      previous = auth;
      return nextState;
    }

    if (auth !== previous) {
      writeChanged(previous, auth);
      previous = auth;
    }
    return nextState;
  };
}

/**
 * Writes only the fields that actually changed, in each key's original encoding.
 *
 * The encodings are load-bearing: `auth-token` stays quote-wrapped because the
 * Playwright storageState fixture reads it raw, and `collab-org` stays an
 * unquoted bare string because the interceptor forwards it straight into a
 * header. Getting either wrong fails silently rather than loudly.
 */
function writeChanged(prev: AuthState, next: AuthState): void {
  if (prev.user !== next.user) writeJson(USER_KEY, next.user);
  if (prev.token !== next.token) writeJson(TOKEN_KEY, next.token);
  if (prev.organisation !== next.organisation) writeJson(USER_ORG_KEY, next.organisation);
  if (prev.permissions !== next.permissions) {
    // An empty map is the signed-out value, and the key is removed rather than
    // left behind as "{}" - a leftover key after sign-out is the exact thing this
    // is meant to prevent. The empty ARRAY is different: it is the in-flight value
    // during a permission load, and was always persisted as "[]".
    writeJson(PERMISSION_KEY, hasPermissions(next.permissions) ? next.permissions : null);
  }
  if (prev.currentProject !== next.currentProject) writeJson(PROJECT_KEY, next.currentProject);
  if (prev.collabOrgId !== next.collabOrgId) writeRaw(COLLAB_ORG_KEY, next.collabOrgId);
}

function hasPermissions(permissions: AuthState['permissions']): boolean {
  if (!permissions) return false;
  if (Array.isArray(permissions)) return true;
  return Object.keys(permissions).length > 0;
}
